import isUndefined from 'lodash/isUndefined';
import { memo } from 'react';
import { Text, View } from 'react-native';

import { isWebPlatform } from '../../../boot/utils';
import { AnalyticsEvent } from '../../../constants/AnalyticsEvent';
import { MktType } from '../../../constants/MktType';
import { defaultSortBy } from '../../../constants/sortData';
import withDisplayImpression from '../../../decorators/withDisplayImpression';
import Image from '../../../elements/Image';
import { FastImageSource } from '../../../elements/Image/FastImage';
import Placeholder from '../../../elements/Placeholder';
import Touchable from '../../../elements/Touchable';
import useActions from '../../../hooks/useActions';
import { logAnalyticsEvent } from '../../../redux/actions/analyticsLog';
import { AdditionalCategory } from '../../../types/Categories';
import { CatItem } from '../../../types/CatsResponse';
import { WebLinkOpenType } from '../../../types/Common';
import Configs from '../../../utils/configs';
import * as navigationService from '../../../utils/navigationService';
import pageNavigation from '../../../utils/pageNavigation';
import routeUtils from '../../../utils/routes';
import styles from './styles';

interface Props {
  pairIndex: number;
  layoutName: string;
  index: number;
  shouldDisplayImpressionForDebug: boolean;
  item?: CatItem | AdditionalCategory;
}

const isAdditional = (item): item is AdditionalCategory => {
  return typeof item.href !== 'undefined';
};

const isInternal = (item): item is AdditionalCategory => {
  return new RegExp(
    `https?://((${Configs.MHOST})|(${Configs.REACT_APP_HOST}))/`.replace(
      '.',
      '\\.'
    )
  ).test(item.href || '');
};

const getDiverseDataFromItem: (item: CatItem | AdditionalCategory) => {
  imageSource: NodeRequire | FastImageSource;
  href: string;
  target: WebLinkOpenType;
} = item => {
  const isAdditionalLink = isAdditional(item);
  const isInternalLink = isInternal(item);

  switch (true) {
    case isAdditionalLink: {
      return {
        imageSource: (item as AdditionalCategory).imageSource,
        href: (item as AdditionalCategory).href,
        target: isInternalLink ? WebLinkOpenType.SELF : WebLinkOpenType.BLANK,
      };
    }
    default: {
      const routeInfo = routeUtils.getPathAndParamsFromData('ListPage', {
        typeId: item.id,
        sortBy: defaultSortBy,
      });

      return {
        imageSource: { uri: (item as CatItem).icon_url },
        href: routeInfo.fullPath,
        target: WebLinkOpenType.SELF,
      };
    }
  }
};

const Item = memo(({ item, pairIndex, index, layoutName }: Props) => {
  const logEvent = useActions(logAnalyticsEvent);

  if (isUndefined(item)) {
    return null;
  }

  const showLoading = /blank/.test((item as CatItem).id.toString());

  if (showLoading) {
    return (
      <View testID={`loading_${index}_${pairIndex}`} style={styles.container}>
        <Placeholder width={50} height={50} boxStyle={styles.iconLoading} />
        <Placeholder width={37} height={14} />
      </View>
    );
  }

  const { imageSource, href, target } = getDiverseDataFromItem(item);

  const handlePress = () => {
    logEvent({
      logEvent: AnalyticsEvent.Click,
      data: {
        pitNumber: index * 2 + pairIndex + 1,
        layoutName,
        mktType: MktType.Icon,
        additionalLogData: {
          parent_category_id: `${item.parent_id || 0}`,
          category_id: `${item.id}`,
          name: item.name,
        },
      },
    });
    logEvent({
      logEvent: AnalyticsEvent.HomePageClick,
      data: {
        layoutName,
        title: item.name,
        index: index * 2 + pairIndex + 1,
      },
    });

    const isAdditionalLink = isAdditional(item);
    const isInternalLink = isInternal(item);

    if (isAdditionalLink && !isInternalLink && isWebPlatform) {
      return;
    }

    switch (true) {
      case isAdditionalLink && !isInternalLink: {
        navigationService.navigate('WebViewPage', {
          title: item.name,
          url: (item as AdditionalCategory).href,
          screenViewName: item.id,
        });
        break;
      }
      case isAdditionalLink && isInternalLink: {
        pageNavigation((item as AdditionalCategory).href);
        break;
      }
      default: {
        navigationService.navigate('ListPage', {
          typeId: item.id,
          sortBy: defaultSortBy,
        });
        break;
      }
    }
  };

  return (
    <Touchable
      style={styles.container}
      onPress={handlePress}
      testID={`categoryPair_${index}_${pairIndex}`}
      accessibilityRole="link"
      target={target}
      href={href}
    >
      <View style={styles.iconContainer}>
        <Image
          style={styles.iconBackground}
          source={require('./assets/img_category_bg.png')}
        />
        <Image style={styles.icon} source={imageSource} />
      </View>
      <Text style={styles.name} maxFontSizeMultiplier={1}>
        {item.name}
      </Text>
    </Touchable>
  );
});

export default withDisplayImpression<Props>(Item);
